export const appIds = [
  "customer",
  "admin",
  "hotel",
  "merchant",
  "delivery-owner",
  "delivery-rider",
  "taxi",
] as const;

export const platformOwner = "13/ Taung Gyi Organization";

export type AppId = (typeof appIds)[number];

export interface AppFeature {
  readonly title: string;
  readonly description: string;
  readonly status: "available" | "launch" | "later";
}

export interface AppConfig {
  readonly id: AppId;
  readonly roleLabel: string;
  readonly manifestName: string;
  readonly accent: string;
  readonly accentText: string;
  readonly welcome: string;
  readonly features: readonly AppFeature[];
}

export const apps: Record<AppId, AppConfig> = {
  customer: {
    id: "customer",
    roleLabel: "Customer",
    manifestName: "TaungGyi Link",
    accent: "#ffffff",
    accentText: "#08131f",
    welcome: "Our City, Linked.",
    features: [
      {
        title: "Link Stay",
        description: "Browse stays and request a booking.",
        status: "available",
      },
      {
        title: "Link Food",
        description: "Order from trusted local merchants.",
        status: "available",
      },
      {
        title: "Link Delivery",
        description: "Follow the status of active deliveries.",
        status: "launch",
      },
      {
        title: "Link Ride",
        description: "Find verified taxi groups and call directly.",
        status: "available",
      },
    ],
  },
  admin: {
    id: "admin",
    roleLabel: "Administration",
    manifestName: "TaungGyi Link Admin",
    accent: "#000000",
    accentText: "#ffffff",
    welcome: "Operate partners, approvals, notifications, and support safely.",
    features: [
      {
        title: "Approvals",
        description: "Review partners, bookings, and orders.",
        status: "available",
      },
      {
        title: "Operations",
        description: "See urgent actions across every service.",
        status: "launch",
      },
      { title: "Audit", description: "Track sensitive administrative actions.", status: "launch" },
      {
        title: "Reports",
        description: "Monitor activity and manual settlements.",
        status: "launch",
      },
    ],
  },
  hotel: {
    id: "hotel",
    roleLabel: "Link Stay Partner",
    manifestName: "TaungGyi Link Stay",
    accent: "#00b4a6",
    accentText: "#08131f",
    welcome: "Manage rooms, availability, and booking requests.",
    features: [
      {
        title: "Property",
        description: "Maintain profile, policies, and media.",
        status: "available",
      },
      {
        title: "Rooms",
        description: "Manage room types and daily availability.",
        status: "launch",
      },
      {
        title: "Requests",
        description: "Confirm or reject booking requests.",
        status: "available",
      },
      { title: "Reports", description: "Review reservations and activity.", status: "launch" },
    ],
  },
  merchant: {
    id: "merchant",
    roleLabel: "Link Food Partner",
    manifestName: "TaungGyi Link Food",
    accent: "#7ccb5e",
    accentText: "#08131f",
    welcome: "Manage your shop, catalog, and cash-on-delivery orders.",
    features: [
      {
        title: "Store",
        description: "Maintain hours, service area, and status.",
        status: "available",
      },
      {
        title: "Catalog",
        description: "Manage items, prices, and availability.",
        status: "launch",
      },
      { title: "Orders", description: "Acknowledge and prepare new orders.", status: "available" },
      { title: "Records", description: "Review cash and fulfillment history.", status: "launch" },
    ],
  },
  "delivery-owner": {
    id: "delivery-owner",
    roleLabel: "Delivery Owner",
    manifestName: "TaungGyi Link Delivery Owner",
    accent: "#ff6b35",
    accentText: "#08131f",
    welcome: "Coordinate riders, assignments, and delivery records.",
    features: [
      {
        title: "Riders",
        description: "Manage verified riders and availability.",
        status: "available",
      },
      { title: "Assignments", description: "Manually assign delivery jobs.", status: "available" },
      { title: "Zones", description: "Control supported delivery areas.", status: "launch" },
      { title: "Reports", description: "Review jobs, cash, and exceptions.", status: "launch" },
    ],
  },
  "delivery-rider": {
    id: "delivery-rider",
    roleLabel: "Delivery Rider",
    manifestName: "TaungGyi Link Delivery Rider",
    accent: "#ff6b35",
    accentText: "#08131f",
    welcome: "Receive assignments and complete deliveries reliably.",
    features: [
      {
        title: "Available jobs",
        description: "See assignments from your owner.",
        status: "available",
      },
      {
        title: "Active delivery",
        description: "Update pickup and delivery status.",
        status: "launch",
      },
      { title: "Proof", description: "Record safe proof of delivery.", status: "launch" },
      { title: "History", description: "Review completed delivery records.", status: "launch" },
    ],
  },
  taxi: {
    id: "taxi",
    roleLabel: "Taxi Driver",
    manifestName: "TaungGyi Link Ride",
    accent: "#ffc107",
    accentText: "#08131f",
    welcome: "Manage your ride requests, vehicle availability, and in-app calls.",
    features: [
      {
        title: "In-app calls",
        description: "Talk to customers without sharing your phone number.",
        status: "available",
      },
      {
        title: "Drivers",
        description: "View your verified driver and vehicle profile.",
        status: "available",
      },
      {
        title: "Records",
        description: "Accept and complete your ride requests.",
        status: "launch",
      },
      {
        title: "Reports",
        description: "Review your completed rides and income.",
        status: "launch",
      },
    ],
  },
};

export function getAppConfig(id: AppId): AppConfig {
  return apps[id];
}
